/**
 * Shell top bar — the ONE compact strip between the command header and the
 * workspace content (mockup contract, merged 2026-08-08). Left: the active
 * incident's identity, location, authoritative lifecycle state pill and active
 * role. Right: the active workspace's preset label/picker and layout actions
 * (save/role default), fed by whichever `FlexWorkspaceView` mount is currently
 * active through `WorkspaceLayoutActionsContext` — this component owns no
 * workspace state itself.
 *
 * Incident SELECTION no longer happens here: switching the active incident
 * is a table/list action (Active incidents, Incidents), not a shell-level
 * picker. This bar keeps the resulting incident number, title and location
 * visible after that selection surface collapses.
 *
 * Rendered for authenticated operational roles on desktop/tablet whenever
 * there is an incident status to show OR a workspace bar is registered. Pure
 * administration (no workspace registered) and mobile (FlexLayout never
 * loads, ADR-UI-003) collapse the row entirely. The `.app-shell` grid keeps
 * its fixed-viewport contract with the bar present or absent (the bar row is
 * `auto` → 0).
 */
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { useAppStores } from "./app-context";
import { useWorkspaceLayoutActions } from "./shell/workspace-layout-actions";
import { rolePerspectiveAssignment } from "../features/operations/role-perspective-registry";
import {
  INCIDENT_STATE_PRESENTATION,
  presentIncidentState,
  type IncidentState,
} from "../domain/status-presentation";
import { Button } from "../ui/button";
import { IconButton } from "../ui/icon-button";
import { StatusPill } from "../ui/status-pill";
import { OperationalIcon } from "../ui/operational-icon";
import { workContextLabel } from "./work-context-labels";
import { workspaceText } from "../workspace/i18n";

export const IncidentContextBar = observer(function IncidentContextBar(): ReactNode {
  const { locale, operationalContext, session } = useAppStores();
  const location = useLocation();
  const { actions: workspaceBar } = useWorkspaceLayoutActions();
  const incident = operationalContext.incident;
  const roleCode = session.activeContext?.roleCode ?? null;
  const roleAssignment = rolePerspectiveAssignment(roleCode);
  const isOperationalDesktopOrTablet =
    session.clientDevice !== "mobile" &&
    roleAssignment?.perspective !== "ADMIN" &&
    !location.pathname.startsWith("/administration") &&
    session.canDiscover(["mission_read"]);
  const showIncidentStatus =
    session.status === "authenticated" && isOperationalDesktopOrTablet;
  if (!showIncidentStatus && workspaceBar === null) return null;

  const statePresentation = incident !== null && incident.state in INCIDENT_STATE_PRESENTATION
    ? presentIncidentState(incident.state as IncidentState, locale.code)
    : null;

  return (
    <div
      aria-label={locale.text("shellContextBarLabel")}
      className="incident-context-bar"
      role="region"
    >
      <div className="incident-context-bar__status">
        {!showIncidentStatus ? null : (
          <>
            {incident === null ? (
              <span className="incident-context-bar__empty">
                {locale.text("shellSelectActiveIncident")}
              </span>
            ) : (
              <span className="incident-context-bar__incident">
                <span className="incident-context-bar__identity">
                  <span className="incident-context-bar__level">
                    {locale.text(incident.incidentKind === "REGIONAL_PARENT"
                      ? "shellContextLevelRegional"
                      : "shellContextLevelLocal")}
                  </span>
                  {incident.incidentNumber === null ? null : (
                    <code>{incident.incidentNumber}</code>
                  )}
                  <strong title={incident.title}>{incident.title}</strong>
                </span>
                <span
                  className="incident-context-bar__location"
                  title={incident.locationText ?? locale.text("shellIncidentLocationPending")}
                >
                  <OperationalIcon name="location" />
                  <span>
                    {incident.locationText ?? locale.text("shellIncidentLocationPending")}
                  </span>
                </span>
              </span>
            )}
            {/* ACTIVE is a lifecycle state, not proof of field operations.
                Until the server exposes an evidence-derived phase, the shell
                presents only the authoritative lifecycle state. */}
            {statePresentation === null ? null : (
              <StatusPill label={statePresentation.label} tone={statePresentation.tone} />
            )}
            {roleCode === null ? null : (
              <span className="incident-context-bar__role">
                {locale.text("shellContextRoleLabel")}: <b>{workContextLabel(roleCode, locale.code)}</b>
              </span>
            )}
          </>
        )}
      </div>
      {workspaceBar === null ? null : (
        <div className="incident-context-bar__workspace">
          <span className="thw-workspace-bar__context">
            <span aria-hidden="true" className="thw-workspace-bar__glyph">
              <OperationalIcon name="workspace" />
            </span>
            <span className="thw-workspace-bar__prefix">{workspaceText(locale.code, "workspaceBarPrefix")}</span>
            {workspaceBar.presetChoices === undefined ? (
              <strong className="thw-workspace-bar__label">{workspaceBar.barLabel}</strong>
            ) : (
              <details className="thw-workspace-bar__picker">
                <summary aria-label={workspaceText(locale.code, "presetPicker")}>
                  <strong className="thw-workspace-bar__label">{workspaceBar.barLabel}</strong>
                  <span aria-hidden="true" className="thw-workspace-bar__picker-caret">▾</span>
                </summary>
                <ul className="thw-workspace-bar__picker-menu">
                  {workspaceBar.presetChoices.map((choice) => (
                    <li key={choice.id}>
                      <Button
                        aria-pressed={choice.active}
                        className="thw-workspace-bar__picker-option"
                        onClick={(event) => {
                          event.currentTarget.closest("details")?.removeAttribute("open");
                          workspaceBar.onSelectPreset?.(choice.id);
                        }}
                        variant="ghost"
                      >
                        <span aria-hidden="true" className="thw-tab-icon">
                          {choice.icon}
                        </span>
                        <span className="thw-workspace-bar__picker-copy">
                          <span className="thw-workspace-bar__picker-name">
                            {choice.label}
                            {choice.roleDefault === true ? (
                              <em>{workspaceText(locale.code, "presetRoleDefault")}</em>
                            ) : null}
                          </span>
                          {choice.description === undefined ? null : (
                            <small>{choice.description}</small>
                          )}
                        </span>
                      </Button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </span>
          <span className="thw-workspace-bar__actions">
            <IconButton
              className="thw-workspace-bar__action"
              icon={<OperationalIcon name="save" />}
              label={workspaceText(locale.code, "saveLayout")}
              onClick={() => {
                void workspaceBar.save();
              }}
              variant="ghost"
            />
            <IconButton
              className="thw-workspace-bar__action"
              icon={<OperationalIcon name="restore" />}
              label={workspaceText(locale.code, "roleDefault")}
              onClick={workspaceBar.reset}
              variant="ghost"
            />
          </span>
        </div>
      )}
    </div>
  );
});
